import React, { useEffect, useState } from "react";
import { Button, Skeleton, FormControl } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAppDispatch } from "../../../hooks/useAppDispatch.tsx";
import { useAppSelector } from "../../../hooks/useAppSelector.tsx";
import { fetchDashboardData } from "../../../redux/features/dashboard/dashboardAnalyticsSlice.tsx";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/de";
import utc from "dayjs/plugin/utc";
import { toast } from "react-toastify";

const DashboardPage = () => {
  const dispatch = useAppDispatch();

  // data to show when the array is empty from backend
  const zeroEventData = [
    { title: "User Engagement", value: "0" },
    { title: "Screen View", value: "0" },
    { title: "App Open", value: "0" },
    { title: "Session Start", value: "0" },
    { title: "First Open", value: "0" },
    { title: "Create Booking", value: "0" },
    { title: "App Remove", value: "0" },
    { title: "App Exception", value: "0" },
    { title: "Notification Open", value: "0" },
    { title: "Notification Dismiss", value: "0" },
  ];

  // data to show when the array is empty from backend
  const zeroCustomMetricData = [
    { title: "Active Users", value: 0 },
    { title: "New Users", value: 0 },
    { title: "Average Session Duration", value: "0 Seconds" },
  ];

  const { data, loading, error } = useAppSelector((state) => state.dashboard);

  const [selectedTab, setSelectedTab] = useState<string>("Yesterday");
  const [customStartDate, setCustomStartDate] = useState<Dayjs | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Dayjs | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  const tabs = ["Today", "Yesterday", "This month", "Last 3 months", "Custom"];
  dayjs.extend(utc);

  const fetchData = (
    tab: string,
    start: Dayjs | null = null,
    end: Dayjs | null = null
  ) => {
    let dateRange;

    if (tab === "Custom" && start && end) {
      dateRange = {
        fromDate: start.utc().startOf("day").toISOString(),
        toDate: end.utc().endOf("day").toISOString(),
      };
    } else {
      const endDate = dayjs().utc();
      let startDate = dayjs().utc();

      switch (tab) {
        case "Today":
          startDate = endDate.startOf("day");
          break;
        case "Yesterday":
          startDate = endDate.subtract(1, "day").startOf("day");
          endDate.subtract(1, "day").endOf("day");
          break;
        case "This month":
          startDate = endDate.subtract(30, "day");
          break;
        case "Last 3 months":
          startDate = endDate.subtract(90, "day");
          break;
        default:
          break;
      }

      dateRange = {
        fromDate: startDate.toISOString(),
        toDate: endDate.toISOString(),
      };
    }

    dispatch(fetchDashboardData(dateRange));
  };
  useEffect(() => {
    fetchData("Yesterday");
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error); // Display the error message using toast
    }
  }, [error]);

  const handleTabClick = (tab: string) => {
    setSelectedTab(tab);
    if (tab !== "Custom") {
      setCustomStartDate(null);
      setCustomEndDate(null);
      setDateError(null); // Clear any existing error when switching tabs
      fetchData(tab);
    }
  };

  const handleDateChange = (type: "start" | "end", newValue: Dayjs | null) => {
    if (type === "start") {
      setCustomStartDate(newValue);
      setDateError(null); // Clear error when changing start date
    } else {
      setCustomEndDate(newValue);
      if (customStartDate && newValue) {
        if (newValue.isBefore(customStartDate)) {
          setDateError("To Date cannot be earlier than From Date");
        } else {
          setDateError(null);
          fetchData("Custom", customStartDate, newValue);
        }
      }
    }
  };

  // Prepare data for display with default values
  const dailyAndMonthlyUsers = [
    ...(data?.dailyActiveUsers?.data || []).map((platform) => ({
      title: `${platform.platform} Daily Users`,
      value: platform.activeUsers,
      icon: platform.platform.toLowerCase(),
    })),
    ...(data?.monthlyActiveUsers?.data || []).map((platform) => ({
      title: `${platform.platform} Monthly Users`,
      value: platform.activeUsers,
      icon: platform.platform.toLowerCase(),
    })),
  ];

  const eventData = (data?.eventData || [])
    .filter(
      (event) =>
        ![
          "notification_receive",
          "notification_foreground",
          "app_exception",
          "notification_dismiss",
        ].includes(event.eventName)
    )
    .map((event) => ({
      title: event.eventName
        .toLowerCase()
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      value: event.eventCount,
      bgColor: "bg-[#A9EDF382]",
    }));

  // Prepare customDateMetrics data for display
  const customMetrics = data?.customDateMetrics?.[0]
    ? [
      {
        title: "Active Users",
        value: data.customDateMetrics[0].activeUsers,
        bgColor: "bg-[#A9EDF382]",
      },
      {
        title: "New Users",
        value: data.customDateMetrics[0].newUsers,
        bgColor: "bg-[#A9EDF382]",
      },
      {
        title: "Average Session Duration",
        value: `${(
          parseFloat(data.customDateMetrics[0].averageSessionDuration) / 1000
        ).toFixed(2)} seconds`,
        bgColor: "bg-[#A9EDF382]",
      },
    ]
    : [];

  const renderSkeletons = () => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mt-4">
        {Array.from({ length: 15 }).map((_, index) => (
          <div key={index} className="p-6 rounded-md shadow-md">
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rectangular" height={60} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-normal md:justify-end items-center gap-2 h-[100px]  overflow-x-scroll md:overflow-x-auto overflow-y-hidden">
        {tabs.map((tab) => (
          <Button
            key={tab}
            sx={{
              height: "55px",
              color: "black",
              minWidth: 120,
              border: "1px solid #C4C4C4",
              borderRadius: "8px",
              fontWeight: "bold",
              textTransform: "none", // Disable uppercase
              padding: "6px 16px",
              marginRight: "12px",
              whiteSpace: "nowrap",
            }} // Ensure consistent width
            variant={selectedTab === tab ? "contained" : "outlined"}
            onClick={() => handleTabClick(tab)}
          >
            {tab}
          </Button>
        ))}
        {selectedTab === "Custom" && (
          <div className="flex flex-row lg:flex-row items-center gap-2 h-full">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl sx={{ minWidth: 120, height: 56 }}>
                {" "}
                {/* Fixed height */}
                <DatePicker
                  maxDate={dayjs()}
                  minDate={dayjs("2000-01-01")}
                  label="From Date"
                  value={customStartDate}
                  onChange={(newValue) => handleDateChange("start", newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      style: { minWidth: 120 }, // Match button width
                    },
                  }}
                />
              </FormControl>

              <span>-</span>

              <FormControl sx={{ minWidth: 120, height: 56 }}>
                {" "}
                {/* Fixed height */}
                <DatePicker
                  maxDate={dayjs()}
                  minDate={dayjs("2000-01-01")}
                  label="To Date"
                  value={customEndDate}
                  onChange={(newValue) => handleDateChange("end", newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      style: { minWidth: 120 }, // Match button width
                      error: !!dateError,
                      helperText: dateError,
                    },
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </div>
        )}
      </div>

      {loading ? (
        renderSkeletons()
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mt-4">
            {eventData && eventData.length > 0
              ? eventData.map((item, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-md shadow-md ${item.bgColor} flex flex-col items-start gap-3 md:gap-6`}
                >
                  <span className="text-xs md:text-base font-medium text-[#59A3AA]">
                    {item.title}
                  </span>
                  <span className="text-4xl md:text-[44px] font-bold text-[#3D3A3A]">
                    {item.value}
                  </span>
                </div>
              ))
              : zeroEventData.map((item, index) => (
                <div
                  key={index}
                  className="p-4 rounded-md shadow-md bg-[#A9EDF382] flex flex-col items-start gap-3 md:gap-6"
                >
                  <span className="text-xs md:text-base font-medium text-[#59A3AA]">
                    {item.title}
                  </span>
                  <span className="text-4xl md:text-[44px] font-bold text-[#3D3A3A]">
                    {item.value}
                  </span>
                </div>
              ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 min-h-[153px]">
            {customMetrics && customMetrics.length > 0
              ? customMetrics.map((item, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-md shadow-md flex flex-col items-start gap-8 md:gap-11 `}
                >
                  <span className="text-xs md:text-base font-medium text-[#59A3AA]">
                    {item.title}
                  </span>
                  <span className="text-4xl md:text-[44px] font-bold text-[#3D3A3A]">
                    {item.value}
                  </span>
                </div>
              ))
              : zeroCustomMetricData.map((item, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-md shadow-md flex flex-col items-start gap-8 md:gap-11 `}
                >
                  <span className="text-xs md:text-base font-medium text-[#59A3AA]">
                    {item.title}
                  </span>
                  <span className="text-4xl md:text-[44px] font-bold text-[#3D3A3A]">
                    {item.value}
                  </span>
                </div>
              ))}
          </div>
        </>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 min-h-[153px]">
        {dailyAndMonthlyUsers.map((item, index) => (
          <div
            key={index}
            className={`p-4 rounded-md shadow-md  flex flex-col items-start gap-8 md:gap-11 `}
          >
            <span className="text-xs md:text-base font-medium text-[#59A3AA] flex felx-row justify-between w-full">
              {item.title}
              {item.icon && (
                <span className="text-xl">
                  {item.icon === "android" && (
                    <img
                      src="/assets/android-Icon.png"
                      className="h-6"
                      alt="android icon"
                    />
                  )}
                  {item.icon === "ios" && (
                    <i className="fab fa-apple text-[#0ABAB5]" />
                  )}
                </span>
              )}
            </span>
            <span className="text-4xl md:text-[44px] font-bold text-[#3D3A3A]">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardPage;
